import { z } from "zod";

/**
 * Prospect providers the outbound launch pipeline can query. Shared between
 * the task payload schema, the trigger service, the router, and the UI so
 * adding a third provider is a one-line change here.
 */
export const OUTBOUND_PROVIDERS = [
  "SMARTLEAD",
  "CONTACTOUT",
  "AGGKNOWLEDGE",
] as const;
export type OutboundProvider = (typeof OUTBOUND_PROVIDERS)[number];

/**
 * Lifecycle of an outbound campaign's recruitment link, stored in
 * `channelDetails.lifecycle`. DRAFT = prepared (Smartlead campaign + sequence +
 * schedule exist) but no leads uploaded and not sending; LAUNCHED = leads
 * uploaded and (in production) sending. Shared so the launch tasks that write
 * it and the review UI that reads it can't drift on a bare string literal.
 */
export const OUTBOUND_LIFECYCLE = {
  DRAFT: "DRAFT",
  LAUNCHED: "LAUNCHED",
} as const;
export type OutboundLifecycle =
  (typeof OUTBOUND_LIFECYCLE)[keyof typeof OUTBOUND_LIFECYCLE];

/**
 * What built the audience behind an outbound campaign, stored in
 * `channelDetails.source` by the BYO prepare task and stamped onto every
 * `OutboundLead.source` that task writes.
 *
 * `internal-leads` is a study's own lot, assembled on the Leads tab out of the
 * pool Terac already paid for. It is the only source that tab renders: a
 * vendor-sourced campaign and a hand-uploaded CSV (which stamps `csv`) are a
 * different audience with a different owner, and they stay on Overview, where
 * every channel of a study is reviewable. Shared so the task that writes the
 * field and the surfaces that read it can't drift on a bare string literal.
 */
export const INTERNAL_LEADS_SOURCE = "internal-leads";

/**
 * One bring-your-own-leads lead after client-side CSV column mapping. Only
 * `email` is required; the rest are optional enrichment. Shared by the router
 * input, the prepare use-case, and the Trigger task payload so the shape can't
 * drift across the three layers.
 */
export const byoLeadSchema = z.object({
  email: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  companyName: z.string().optional(),
  companyDomain: z.string().optional(),
  jobTitle: z.string().optional(),
  linkedinUrl: z.string().optional(),
  location: z.string().optional(),
  // Exports often split the city and the country across two columns. Mapped
  // separately so the country is available for schedule resolution
  // (resolveOutboundTimezone) even when `location` is only a city.
  country: z.string().optional(),
  // Carried so a cold caller has something to dial. Outbound itself never
  // sends it anywhere: it is not part of the Smartlead upload payload.
  phone: z.string().optional(),
});
export type ByoLead = z.infer<typeof byoLeadSchema>;

// Prospects to email per desired response. Cold outbound converts at roughly
// 2% emailed-to-completed, so ~50 leads per target response. Tune this single
// knob to change campaign sizing.
export const OUTBOUND_LEAD_MULTIPLIER = 50;

/**
 * Per-campaign hard ceiling on prospects: a cost/sanity bound, NOT a Smartlead
 * or deliverability limit. Each lead costs search + email-reveal credits plus
 * rerank tokens, so this caps per-campaign spend. Deliverability is governed
 * separately by the per-mailbox daily limit across the warmed pool, and the
 * upload itself is batched (Smartlead caps lead imports per request). Consumed
 * by the trigger task (cap) and the UI help text.
 */
export const SMARTLEAD_MAX_LEADS_PER_CAMPAIGN = 2500;

/**
 * New leads entering a single campaign's sequence per day. NOT the binding
 * constraint on total volume: the real governor is the pool of warmed mailboxes
 * (~900 x 25/day = ~22,500 sends/day, i.e. ~7,500 new leads/day across ALL
 * campaigns since each lead gets 3 emails). This per-campaign value just paces
 * one campaign's fill when the pool has spare capacity; set to
 * SMARTLEAD_MAX_LEADS_PER_CAMPAIGN so one campaign can drain its whole allowed
 * load in a day when the pool has room, without the per-campaign cap becoming
 * the bottleneck. Even then a 2500-lead campaign is only ~8 first-touches per
 * mailbox across the pool (well under 25/day). Raise total capacity by adding
 * mailboxes, not this number.
 */
export const SMARTLEAD_MAX_NEW_LEADS_PER_DAY = 2500;

/**
 * Minimum age (days since warmup started) before a Smartlead mailbox is eligible
 * to send real outbound. Smartlead warms new mailboxes for ~2 weeks and advises
 * against production sending until then; using a day-0 domain for cold outreach
 * torches its reputation. A mailbox warmed outside Smartlead has no Smartlead
 * warmup start, so its age counts from the day it was added to Smartlead.
 * Campaigns attach every connected mailbox in the sending pool, so this is the
 * single gate that keeps unwarmed accounts out of the sending rotation. The
 * check is self-healing: a mailbox auto-joins the pool the day it crosses this
 * threshold, no code or config change needed.
 */
export const SMARTLEAD_MIN_WARMUP_DAYS = 14;

/**
 * Smartlead tag on the mailboxes replacing the burned Mailreef fleet: once one
 * is warm, only tagged mailboxes send.
 */
export const OUTBOUND_SENDING_POOL_TAG = "pool:inboxkit";

/**
 * Days after any campaign emails a person before another campaign may. Sixty
 * holds anyone to about six cold campaigns a year, whatever number of studies
 * happen to want them.
 */
export const OUTBOUND_CONTACT_COOLDOWN_DAYS = 60;

/**
 * When the outbound bounce guard trips a live campaign. Every campaign sends
 * from one shared mailbox pool, so a campaign bouncing past either rate spends
 * reputation every other study depends on.
 */
// Below 100 emails, one or two dead addresses decide either rate.
export const OUTBOUND_BOUNCE_GUARD_MIN_SENT = 100;
// Recipient bounces measure the list: Smartlead's platform median is 1.54%, and
// its worst quarter of campaigns starts at 3.21%.
export const OUTBOUND_BOUNCE_GUARD_RECIPIENT_RATE = 0.03;
// Sender bounces measure our mailboxes being refused (reputation blocks), which
// carry a baseline no list can fix, so they get a wider margin.
export const OUTBOUND_BOUNCE_GUARD_SENDER_RATE = 0.05;

export interface OutboundBounceCounts {
  sentCount: number;
  bounceCount: number;
  senderBounceCount: number;
}

export interface OutboundBounceReading {
  recipientBounceRate: number;
  senderBounceRate: number;
  tripped: boolean;
}

/**
 * Split a campaign's bounces into the recipient side (the address does not
 * exist) and the sender side (our mailbox was refused), and say whether either
 * crossed its rate on a big enough sample. Smartlead's `bounce_count` includes
 * the sender-side ones, so they are subtracted rather than added.
 */
export function readOutboundBounces(
  counts: OutboundBounceCounts,
): OutboundBounceReading {
  if (counts.sentCount <= 0) {
    return { recipientBounceRate: 0, senderBounceRate: 0, tripped: false };
  }
  const recipientBounceRate =
    Math.max(counts.bounceCount - counts.senderBounceCount, 0) /
    counts.sentCount;
  const senderBounceRate = counts.senderBounceCount / counts.sentCount;
  const tripped =
    counts.sentCount >= OUTBOUND_BOUNCE_GUARD_MIN_SENT &&
    (recipientBounceRate >= OUTBOUND_BOUNCE_GUARD_RECIPIENT_RATE ||
      senderBounceRate >= OUTBOUND_BOUNCE_GUARD_SENDER_RATE);
  return { recipientBounceRate, senderBounceRate, tripped };
}

/**
 * The fingerprint of the email-security rendering fleet that inflates outbound
 * landing counts: a 1920x1080 viewport reporting `America/Los_Angeles`.
 *
 * User-agent matching cannot catch these. They present as ordinary desktop
 * Chrome (`Chrome/142.0.7444.175` on `Windows NT 10.0`), which is why
 * `isNonHumanClient` lets them through. The browser timezone is the tell:
 * geoip places these requests in GB, NL, IE and FR while the browser reports
 * Pacific time regardless, which no real visitor does. Supporting evidence in
 * the same traffic: pinned 4-part Chromium build strings, and `Windows NT 6.1`
 * (Windows 7) running Chrome 140, a combination that does not exist.
 *
 * Measured over Jul 1 - Aug 4 2026: this pair matched 906 of 1,233 landing
 * sessions on outbound links, and those 906 produced 1 signup, 1 entry and 1
 * submission between them, against 86 / 16 / 16 from the other 327. It also
 * matched 155 sessions on links that looked clean, so real human landings in
 * the window were ~327 rather than the 427 the raw numbers implied.
 */
const AUTOMATED_LANDING_SCREEN = "1920x1080";
const AUTOMATED_LANDING_TIMEZONE = "America/Los_Angeles";

/**
 * Whether an outbound landing came from an automated renderer rather than a
 * person, so the funnel's landing denominator can exclude it.
 *
 * Applied at read time against the raw fingerprint on the row, never stored as a
 * flag: the rule above is a heuristic about someone else's infrastructure and
 * will need revising, and a stored boolean would need a migration and a backfill
 * to revise.
 *
 * A lead that produced a submission is never classified automated, whatever its
 * fingerprint. Converting is proof of a person, and without this the rule costs
 * a real conversion for every genuine Pacific-timezone visitor on a 1080p
 * monitor, one of the 17 in the measurement window.
 *
 * A landing with no fingerprint is treated as human: either the visitor was
 * already authenticated (a session proves a person, and that path redirects
 * before any client component mounts) or the beacon did not run. Both are
 * better counted than silently dropped.
 */
export function isAutomatedLanding(lead: {
  landingScreen: string | null;
  landingTimezone: string | null;
  submissionId: string | null;
}): boolean {
  if (lead.submissionId) {
    return false;
  }
  return (
    lead.landingScreen === AUTOMATED_LANDING_SCREEN &&
    lead.landingTimezone === AUTOMATED_LANDING_TIMEZONE
  );
}

/**
 * What a study actually asks a participant to do, for the cold email's pay line
 * ("$40 for a 15-minute ___").
 *
 * Derived from the task plan rather than from prose. The generator used to let
 * the copy LLM infer this from the study description, which was wrong twice over:
 * it can only guess when the description does not state a format, and it is not
 * even stable when the description does. The same ServiceNow study, whose
 * description reads "a 30-minute virtual in-depth interview", produced "video
 * call" on one run and "screen-share session" on the next. Those promise the
 * recipient materially different commitments.
 *
 * The task plan answers it deterministically, and there is a SURVEY type, which
 * is the distinction that matters most: a survey is a far cheaper yes than a call
 * (no scheduling, no camera, nobody to ask).
 *
 * Paperwork steps are skipped. An AGREEMENT or E_SIGNATURE is something a
 * participant signs on the way in, never the thing they are being paid for, so a
 * plan that starts with one must not advertise itself as "a 30-minute agreement".
 */
const SESSION_NOUN_BY_TASK_TYPE: Record<string, string> = {
  // A human-scheduled 1:1. "call" rather than "video call": the provider only
  // tells us it was booked through a scheduler, not which medium it runs on.
  SCHEDULED_INTERVIEW: "call",
  // The platform's own on-platform interview, not a call with the research team.
  INTERVIEW: "interview",
  SURVEY: "survey",
  ACTIVITY: "task",
  FILE_UPLOAD: "task",
  JOIN_SLACK: "conversation",
  SLACK_HANDOFF: "conversation",
  // INSTALL_TIME_TRACKER is left out on purpose: installing a tracker is set-up
  // for the paid work, never the thing being paid for.
};

const PAPERWORK_TASK_TYPES = new Set(["AGREEMENT", "E_SIGNATURE"]);

/**
 * The session noun for a study's task plan, or null when the plan says nothing
 * usable. Null means the caller falls back to a word that is true of any format,
 * never to a guess.
 */
export function resolveSessionNoun(
  taskPlan: { taskType?: string | null }[] | null | undefined,
): string | null {
  for (const task of taskPlan ?? []) {
    const type = task.taskType;
    if (!type || PAPERWORK_TASK_TYPES.has(type)) {
      continue;
    }
    const noun = SESSION_NOUN_BY_TASK_TYPE[type];
    if (noun) {
      return noun;
    }
  }
  return null;
}

/**
 * Smartlead AI reply-category ids (from GET /leads/fetch-categories, stable
 * account-wide). Used by outbound to (a) tell Smartlead which categories its AI
 * should auto-assign, (b) route the leads it marks INTERESTED into the reply
 * that carries the study link and (c) suppress the leads it marks
 * NOT_INTERESTED or DO_NOT_CONTACT.
 */
export const SMARTLEAD_LEAD_CATEGORY = {
  INTERESTED: 1,
  MEETING_REQUEST: 2,
  NOT_INTERESTED: 3,
  DO_NOT_CONTACT: 4,
  INFORMATION_REQUEST: 5,
  OUT_OF_OFFICE: 6,
  WRONG_PERSON: 7,
} as const;

/**
 * Category NAMES, for the one Smartlead endpoint that rejects the ids above.
 *
 * The two endpoints disagree and both validate strictly, so they cannot share a
 * representation (live-probed 2026-07-27):
 *   - `POST /campaigns/{id}/settings` `ai_categorisation_options` wants NUMBERS
 *     ("must be a number" on a name).
 *   - `POST /campaigns/{id}/webhooks` `categories` wants STRINGS
 *     ("categories[0] must be a string" on an id, then "Invalid category - X"
 *     if the name is not one of the account's categories).
 */
export const SMARTLEAD_LEAD_CATEGORY_NAME = {
  INTERESTED: "Interested",
  NOT_INTERESTED: "Not Interested",
  DO_NOT_CONTACT: "Do Not Contact",
} as const;

/**
 * UI / Zod bound on the user-supplied target response count. Independent from
 * the provider cap, so changing one does not have to move the other.
 */
export const OUTBOUND_MAX_TARGET_COUNT = 250;

/**
 * Converts a target response count into a prospect count via the lead
 * multiplier, bounded by the per-campaign cap. With the current defaults
 * (50x, cap 2500) a target of 50+ hits the cap; a typical target of 20 yields
 * 1000 leads.
 */
export function leadsFromTargetCount(targetCount: number): number {
  return Math.min(
    targetCount * OUTBOUND_LEAD_MULTIPLIER,
    SMARTLEAD_MAX_LEADS_PER_CAMPAIGN,
  );
}

/**
 * Plain-language sizing hint shown on the outbound config surfaces: how many
 * people we'll email and how many responses that yields. Shared so the copy and
 * the reply-rate math can't drift between the quick-launch modal and the
 * add-channel form.
 *
 * The response figure is derived from the (capped) lead count, NOT the raw
 * target: at OUTBOUND_LEAD_MULTIPLIER = 50 (~2% reply rate) the lead count
 * saturates at SMARTLEAD_MAX_LEADS_PER_CAMPAIGN once the target passes 50, so a
 * target of 100-250 can't actually be reached. Computing responses back from
 * the leads keeps the promise honest instead of over-stating it at the top of
 * the range.
 */
export function describeOutboundReach(targetCount: number): string {
  const leads = leadsFromTargetCount(targetCount);
  const expectedResponses = Math.round(leads / OUTBOUND_LEAD_MULTIPLIER);
  return `We'll email about ${leads.toLocaleString()} people to reach ~${expectedResponses.toLocaleString()} responses.`;
}
